import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Folder, FolderPlus, FilePlus, Move, Tag, Share2 } from "lucide-react";

/**
 * FILE EXPLORER simulation. Used by file management, info organisation,
 * sharing, data sensitivity. Events: folder_created, file_renamed,
 * file_moved, permission_set, file_shared, file_classified,
 * file_deleted_unintended, public_link_enabled, wrong_audience,
 * misclassified_confidential_as_public, final_decision.
 */
export default function FileExplorerSim({ scenario, recorder, onFinalize, busy }) {
  const initial = scenario.initial || [];
  const [files, setFiles] = useState(initial.map((f) => ({ ...f, folder: null, classification: null })));
  const [folders, setFolders] = useState([]);
  const [newFolder, setNewFolder] = useState("");
  const [renameId, setRenameId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [shareTarget, setShareTarget] = useState(null);
  const [shareLevel, setShareLevel] = useState("viewer");
  const shareTargets = scenario.share_targets || [];
  const classifications = scenario.classifications || [];

  const createFolder = () => {
    const v = newFolder.trim();
    if (!v) return;
    if (folders.includes(v)) return;
    setFolders([...folders, v]);
    recorder.log("folder_created", v);
    setNewFolder("");
  };

  const move = (fid, folder) => {
    setFiles((p) => p.map((f) => (f.id === fid ? { ...f, folder } : f)));
    recorder.log("file_moved", `${fid}->${folder}`);
  };
  const startRename = (f) => { setRenameId(f.id); setRenameVal(f.name); };
  const commitRename = () => {
    setFiles((p) => p.map((f) => (f.id === renameId ? { ...f, name: renameVal } : f)));
    recorder.log("file_renamed", `${renameId}:${renameVal}`);
    setRenameId(null);
  };
  const classify = (fid, cls) => {
    const f = files.find((x) => x.id === fid);
    setFiles((p) => p.map((x) => (x.id === fid ? { ...x, classification: cls } : x)));
    recorder.log("file_classified", `${fid}:${cls}`);
    if (f?.expected === "confidential" && cls === "public") {
      recorder.log("misclassified_confidential_as_public", fid);
    }
  };

  const setPermission = () => {
    if (!shareTarget) return;
    recorder.log("permission_set", `${shareTarget.id}:${shareLevel}`);
  };
  const share = (file) => {
    if (!shareTarget) return;
    setPermission();
    recorder.log("file_shared", `${file.id}->${shareTarget.id}:${shareLevel}`);
    if (shareTarget.unsafe || shareTarget.id === "public") {
      recorder.log(shareTarget.id === "public" ? "public_link_enabled" : "wrong_audience", `${file.id}:${shareTarget.id}`);
    }
  };

  const finalize = () => {
    // Decision is concept-aware
    const allClassified = files.length > 0 && files.every((f) => !classifications.length || f.classification);
    let decision;
    if (classifications.length) {
      const allCorrect = files.every((f) => f.expected ? f.classification === f.expected : true);
      decision = allClassified && allCorrect ? "all_correct" : "incomplete";
    } else if (shareTargets.length) {
      // sharing scenario — let server check via final-decision detail derived from last share
      // pick the file most recently shared
      const last = files.find((f) => f.sensitive) || files[0];
      const target = shareTarget?.id || "none";
      decision = `shared:${last.id}:${target}:${shareLevel}`;
    } else {
      // organisation scenario
      const allMoved = files.every((f) => f.folder);
      decision = allMoved && folders.length > 0 ? "organised" : "incomplete";
    }
    onFinalize(decision, { files, folders, shareTarget, shareLevel });
  };

  return (
    <div className="grid lg:grid-cols-12 min-h-[480px]" data-testid="file-explorer-sim">
      {/* Tree */}
      <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-[var(--iddl-rule)] p-4">
        <div className="label-mono mb-2">Folders</div>
        <div className="flex gap-2">
          <Input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} className="rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)] h-9" placeholder="New folder name" data-testid="input-new-folder" />
          <Button size="sm" className="btn-ink rounded-none" onClick={createFolder} data-testid="btn-create-folder">
            <FolderPlus size={12} />
          </Button>
        </div>
        <ul className="mt-3">
          <li className={`p-2 border border-[var(--iddl-rule)] mb-1 flex items-center gap-2`}>
            <Folder size={12} /> <span className="text-sm">Desktop</span>
          </li>
          {folders.map((f) => (
            <li key={f} className="p-2 border border-[var(--iddl-rule)] mb-1 flex items-center gap-2" data-testid={`folder-${f}`}>
              <Folder size={12} /> <span className="text-sm">{f}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Files */}
      <div className="lg:col-span-8 p-4">
        <div className="label-mono mb-2">Files</div>
        <div className="grid gap-2">
          {files.map((f) => (
            <div key={f.id} className="paper border border-[var(--iddl-rule)] p-3" data-testid={`file-${f.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <FilePlus size={12} />
                {renameId === f.id ? (
                  <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="h-8 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]" data-testid={`rename-input-${f.id}`} />
                ) : (
                  <span className="font-mono-disp text-sm">{f.name}</span>
                )}
                <span className="label-mono ml-auto">{f.kind}</span>
                {f.sensitive && <span className="label-mono tint-cs border px-1.5 py-0.5">SENSITIVE</span>}
                {f.classification && (
                  <span className={`label-mono px-1.5 py-0.5 border ${f.classification === "confidential" ? "tint-cs" : f.classification === "internal" ? "tint-ai" : "tint-ds"}`}>
                    {f.classification.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {renameId === f.id ? (
                  <Button size="sm" className="btn-ink rounded-none" onClick={commitRename} data-testid={`btn-rename-commit-${f.id}`}>Save name</Button>
                ) : (
                  <Button size="sm" variant="outline" className="rounded-none" onClick={() => startRename(f)} data-testid={`btn-rename-${f.id}`}>
                    <Tag size={12} className="mr-1" /> Rename
                  </Button>
                )}
                {folders.length > 0 && !classifications.length && (
                  <select
                    onChange={(e) => move(f.id, e.target.value)}
                    value={f.folder || ""}
                    className="border border-[var(--iddl-rule)] text-xs px-2 py-1"
                    data-testid={`move-${f.id}`}
                  >
                    <option value="">Move to folder…</option>
                    {folders.map((fd) => <option key={fd} value={fd}>{fd}</option>)}
                  </select>
                )}
                {classifications.length > 0 && classifications.map((cls) => (
                  <Button key={cls} size="sm" variant="outline" className="rounded-none" onClick={() => classify(f.id, cls)} data-testid={`btn-classify-${f.id}-${cls}`}>
                    Mark as {cls}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {shareTargets.length > 0 && (
          <div className="mt-6 paper p-4">
            <div className="label-mono">Share</div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <select className="border border-[var(--iddl-rule)] text-sm px-2 py-1" onChange={(e) => setShareTarget(shareTargets.find((t) => t.id === e.target.value))} data-testid="select-share-target">
                <option value="">Select recipient…</option>
                {shareTargets.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <select className="border border-[var(--iddl-rule)] text-sm px-2 py-1" value={shareLevel} onChange={(e) => setShareLevel(e.target.value)} data-testid="select-share-level">
                <option value="viewer">viewer (read-only)</option>
                <option value="editor">editor</option>
              </select>
              <Button size="sm" variant="outline" className="rounded-none" onClick={setPermission} data-testid="btn-set-permission">
                Set permission
              </Button>
              <Button size="sm" className="btn-ink rounded-none" onClick={() => share(files.find((f) => f.sensitive) || files[0])} data-testid="btn-share">
                <Share2 size={12} className="mr-1" /> Share
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-[var(--iddl-rule)] flex justify-end">
          <Button onClick={finalize} disabled={busy} className="btn-ink rounded-none" data-testid="btn-finalize-files">Finalise — submit organisation</Button>
        </div>
      </div>
    </div>
  );
}
